/**
 * Command palette component using cmdk.
 * Provides quick navigation and actions via Cmd+K / Ctrl+K.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  LayoutDashboard,
  LineChart,
  MessageSquare,
  BookOpen,
  FileText,
  Trophy,
  Calendar,
  MessageCircle,
  HelpCircle,
  RefreshCw,
  Plus,
  Settings,
  Activity,
  Radar,
  Brain,
  Link2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Popular tickers for quick search
const POPULAR_TICKERS = [
  { symbol: 'SPY', name: 'SPDR S&P 500 ETF' },
  { symbol: 'QQQ', name: 'Invesco QQQ Trust' },
  { symbol: 'AAPL', name: 'Apple Inc.' },
  { symbol: 'NVDA', name: 'NVIDIA Corporation' },
  { symbol: 'TSLA', name: 'Tesla, Inc.' },
  { symbol: 'MSFT', name: 'Microsoft Corporation' },
  { symbol: 'AMZN', name: 'Amazon.com, Inc.' },
  { symbol: 'META', name: 'Meta Platforms, Inc.' },
  { symbol: 'GOOGL', name: 'Alphabet Inc.' },
  { symbol: 'IWM', name: 'iShares Russell 2000 ETF' },
];

// Navigation items
const NAVIGATION_ITEMS = [
  { path: '/positions', label: 'Positions', icon: LayoutDashboard, keywords: ['portfolio', 'holdings'] },
  { path: '/research-workflow', label: 'Research', icon: BookOpen, keywords: ['generate', 'ideas', 'ai', 'deep', 'analysis', 'report'] },
  { path: '/performance', label: 'Performance', icon: LineChart, keywords: ['returns', 'pnl', 'profit'] },
  { path: '/trade-tracker', label: 'Trade Tracker', icon: LayoutDashboard, keywords: ['track', 'open', 'trades'] },
  { path: '/chat', label: 'AI Chat', icon: MessageSquare, keywords: ['assistant', 'help', 'question'] },
  { path: '/leaderboard', label: 'Leaderboard', icon: Trophy, keywords: ['rankings', 'best', 'worst'] },
  { path: '/post-mortems', label: 'Post-Mortems', icon: FileText, keywords: ['journal', 'review', 'lessons'] },
  { path: '/calendar', label: 'Calendar', icon: Calendar, keywords: ['events', 'fomc', 'earnings'] },
  { path: '/flow', label: 'Flow', icon: Activity, keywords: ['gex', 'gamma', 'short volume', 'retail'] },
  { path: '/signals', label: 'Signals', icon: Radar, keywords: ['insiders', 'congress', 'contracts', 'alerts'] },
  { path: '/model-health', label: 'Model Health', icon: Brain, keywords: ['ml', 'observability', 'calibration', 'features'] },
  { path: '/trade-chains', label: 'Trade Chains', icon: Link2, keywords: ['rolls', 'chains', 'post mortem', 'sse'] },
  { path: '/feedback', label: 'Feedback', icon: MessageCircle, keywords: ['suggest', 'bug', 'feature'] },
  { path: '/help', label: 'Help', icon: HelpCircle, keywords: ['guide', 'docs', 'documentation'] },
  { path: '/triage', label: 'Triage', icon: LayoutDashboard, keywords: ['screener', 'daily', 'approve', 'reject', 'pie'] },
  { path: '/risk-policies', label: 'Risk Policies', icon: Settings, keywords: ['constraints', 'blacklist', 'limits', 'delta', 'rules'] },
];

// Quick actions
const QUICK_ACTIONS = [
  { id: 'new-research', label: 'Start New Research', icon: Plus, keywords: ['create', 'add', 'trade'] },
  { id: 'trade-doctor', label: 'Run Trade Doctor', icon: Search, keywords: ['diagnose', 'health', 'review', 'doctor'] },
  { id: 'refresh', label: 'Refresh Data', icon: RefreshCw, keywords: ['reload', 'update'] },
  { id: 'settings', label: 'Settings', icon: Settings, keywords: ['preferences', 'config'] },
];

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  // Close handler that resets search state
  const handleClose = useCallback(() => {
    setSearch('');
    onOpenChange(false);
  }, [onOpenChange]);

  // NOTE: Cmd+K keyboard shortcut is handled in App.tsx (single listener)
  // Only handle Escape here for closing when open
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        handleClose();
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [open, handleClose]);

  const handleNavigate = useCallback((path: string) => {
    navigate(path);
    onOpenChange(false);
  }, [navigate, onOpenChange]);

  const handleTickerSelect = useCallback((symbol: string) => {
    navigate(`/research-workflow?ticker=${symbol}`);
    onOpenChange(false);
  }, [navigate, onOpenChange]);

  const handleAction = useCallback((actionId: string) => {
    switch (actionId) {
      case 'new-research':
        navigate('/research-workflow');
        break;
      case 'trade-doctor':
        navigate('/trade-doctor');
        break;
      case 'refresh':
        window.location.reload();
        break;
      case 'settings':
        // DEFERRED: Requires new SettingsModal component — out of scope for tech debt pass
        break;
    }
    onOpenChange(false);
  }, [navigate, onOpenChange]);

  const filteredTickers = useMemo(() => {
    if (!search) return POPULAR_TICKERS.slice(0, 5);
    return POPULAR_TICKERS.filter(
      (t) =>
        t.symbol.toLowerCase().includes(search.toLowerCase()) ||
        t.name.toLowerCase().includes(search.toLowerCase())
    ).slice(0, 5);
  }, [search]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close command palette"
        className="fixed inset-0 cursor-default border-0 bg-black/80 p-0"
        onClick={handleClose}
      />

      {/* Command dialog */}
      <div className="fixed left-1/2 top-[20%] -translate-x-1/2 w-full max-w-lg">
        <Command
          className={cn(
            'rounded-lg border border-border bg-card shadow-2xl overflow-hidden',
            '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground',
            '[&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0',
            '[&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5',
            '[&_[cmdk-input]]:h-12',
            '[&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3',
            '[&_[cmdk-item]_svg]:h-4 [&_[cmdk-item]_svg]:w-4'
          )}
          loop
        >
          {/* Search input */}
          <div className="flex items-center border-b border-border px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <Command.Input
              value={search}
              onValueChange={setSearch}
              placeholder="Search tickers, pages, actions..."
              className={cn(
                'flex h-12 w-full rounded-md bg-transparent py-3 text-sm outline-none',
                'placeholder:text-muted-foreground',
                'disabled:cursor-not-allowed disabled:opacity-50'
              )}
            />
            <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border border-border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
              <span className="text-xs">esc</span>
            </kbd>
          </div>

          {/* Command list */}
          <Command.List className="max-h-[400px] overflow-y-auto overflow-x-hidden p-2">
            <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
              No results found.
            </Command.Empty>

            {/* Tickers */}
            <Command.Group heading="Tickers">
              {filteredTickers.map((ticker) => (
                  <Command.Item
                    key={ticker.symbol}
                    value={`ticker-${ticker.symbol}`}
                    onSelect={() => handleTickerSelect(ticker.symbol)}
                    className={cn(
                      'flex items-center gap-2 rounded-sm px-2 py-2 text-sm cursor-pointer',
                      'aria-selected:bg-secondary aria-selected:text-foreground'
                    )}
                  >
                    <span className="font-mono font-semibold text-primary">
                      {ticker.symbol}
                    </span>
                    <span className="text-muted-foreground truncate">
                      {ticker.name}
                    </span>
                  </Command.Item>
                ))}
            </Command.Group>

            {/* Navigation */}
            <Command.Group heading="Navigate">
              {NAVIGATION_ITEMS.map((item) => (
                <Command.Item
                  key={item.path}
                  value={`nav-${item.label} ${item.keywords.join(' ')}`}
                  onSelect={() => handleNavigate(item.path)}
                  className={cn(
                    'flex items-center gap-2 rounded-sm px-2 py-2 text-sm cursor-pointer',
                    'aria-selected:bg-secondary aria-selected:text-foreground'
                  )}
                >
                  <item.icon className="h-4 w-4 text-muted-foreground" />
                  <span>{item.label}</span>
                </Command.Item>
              ))}
            </Command.Group>

            {/* Quick Actions */}
            <Command.Group heading="Actions">
              {QUICK_ACTIONS.map((action) => (
                <Command.Item
                  key={action.id}
                  value={`action-${action.label} ${action.keywords.join(' ')}`}
                  onSelect={() => handleAction(action.id)}
                  className={cn(
                    'flex items-center gap-2 rounded-sm px-2 py-2 text-sm cursor-pointer',
                    'aria-selected:bg-secondary aria-selected:text-foreground'
                  )}
                >
                  <action.icon className="h-4 w-4 text-muted-foreground" />
                  <span>{action.label}</span>
                </Command.Item>
              ))}
            </Command.Group>
          </Command.List>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-border px-3 py-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">
                Enter
              </kbd>
              <span>to select</span>
            </div>
            <div className="flex items-center gap-2">
              <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">
                esc
              </kbd>
              <span>to close</span>
            </div>
          </div>
        </Command>
      </div>
    </div>
  );
}

// NOTE: useCommandPalette hook was removed - App.tsx manages command palette state directly
// with a single keyboard listener to avoid duplicate event handlers.
